/**
 * RecentActivitySidebar
 *
 * Desktop-only left sidebar shown on the active-game screen when
 * screenWidth >= DESKTOP_BREAKPOINT (1024 px). Renders the 10 most-recent
 * events from state.events using the shared gameLog formatters.
 *
 * Layout contract:
 *   - Width matches the active-turn action panel exactly
 *     (DEFAULT_RESERVED_LEFT_LANDSCAPE on phone landscape /
 *     DEFAULT_RESERVED_LEFT_DESKTOP on desktop) so the map doesn't jump
 *     horizontally on the active↔spectate switch. The sidebar reads
 *     screenWidth itself; callers don't need to pass a reservedLeft to
 *     MapView — MapView's defaultReservedLeft already matches.
 *   - "See all →" link navigates to the existing log page.
 */

import React, { useMemo, useState, useEffect, useCallback, useId } from 'react'
import { View, Text, FlatList, TouchableOpacity, ActivityIndicator, useWindowDimensions } from 'react-native'
import type { NativeSyntheticEvent, NativeScrollEvent } from 'react-native'


import {
  DEFAULT_RESERVED_LEFT_DESKTOP,
  DEFAULT_RESERVED_LEFT_LANDSCAPE,
  DESKTOP_BREAKPOINT,
} from '../../lib/mapDimensions'
import { groupEvents, makeTerritoryResolver } from '../../lib/gameLog'
import { EventLogRow } from './EventLogRow'
import { applyLogFilters, DEFAULT_LOG_FILTERS, hasActiveFilters, type ActivityFilterState } from '../../lib/activityFilters'
import { ChatLogRow, useChatSeenViewability } from './ChatLogRow'

/** Hairline between rows. Module scope so FlatList never re-creates it. */
function RowSeparator() {
  return (
    <View style={{ height: 1, backgroundColor: '#3d4020', marginHorizontal: 12, opacity: 0.4 }} />
  )
}
import { useGameEvents } from '../../hooks/useGameEvents'
import { useGameMessages } from '../../hooks/useChat'
import { EVENT_PAGE_SIZE, shouldLoadMoreEvents, growEventWindow } from '../../lib/eventLogPaging'
import type { GameState } from '../../lib/gameState'
import type { GamePlayerResponse } from '@warcarp/api-types'
import { mergeChatIntoLog, type LogRow } from '../../lib/logFeed'
import { isSelectedLogRow, selectedLogRowIndex, selectedLogWindowSize } from '../../lib/logSelection'
import { useCenteredLogSelection } from '../../hooks/useCenteredLogSelection'

// ─── Constants ────────────────────────────────────────────────────────────────

// Matches the active-turn action-panel width so the map doesn't jump
// horizontally when the turn flips between viewer and opponent.
//   • phone landscape: DEFAULT_RESERVED_LEFT_LANDSCAPE (256)
//   • desktop:         DEFAULT_RESERVED_LEFT_DESKTOP   (380)
// Used by the sidebar itself AND by chrome that needs to sit at the
// same edge (e.g. the floating cog in the game screen).
export function recentActivitySidebarWidth(isDesktop: boolean): number {
  return isDesktop ? DEFAULT_RESERVED_LEFT_DESKTOP : DEFAULT_RESERVED_LEFT_LANDSCAPE
}

// Fixed height of the "Recent activity" header row.
const ACTIVITY_HEADER_HEIGHT = 36

// ─── Component ────────────────────────────────────────────────────────────────

export interface RecentActivitySidebarProps {
  gameId: string
  state: GameState
  players: GamePlayerResponse[]
  /**
   * 'left-sidebar' (default) — fixed-width column with a right border,
   * meant to sit alongside the map in landscape / desktop.
   * 'bottom-panel' — full-width, capped-height block with a top border,
   * meant to live under the map in portrait. Used when an opponent is
   * on turn so the viewer sees the live event log instead of a static
   * "turn in progress" stub.
   */
  orientation?: 'left-sidebar' | 'bottom-panel'
  /**
   * Optional content rendered above the activity-list scroll view, but
   * INSIDE the sidebar's bordered container. Currently used by the
   * game screen to pin contextual controls on top of the
   * landscape sidebar.
   */
  topContent?: React.ReactNode
  /**
   * Called when a single event row is tapped. The handler typically
   * flips the parent game screen into time-machine view at the given
   * event index. When omitted, individual rows are not tappable.
   */
  onViewAtEvent?: (eventIndex: number) => void
  /**
   * Called when the header "See all →" is tapped: grows the in-game log
   * (GameLogSheet). When omitted, the "See all →" affordance is hidden (e.g.
   * inside the snapshot scrubber).
   */
  onSeeAll?: () => void
  /** Hide the "Recent activity" + "See all →" header row (e.g. the spectator
   * feed beneath the map, where the top-bar events icon already opens the log). */
  hideHeader?: boolean
  /**
   * eventIndex currently being viewed in time-machine mode. Rows that
   * match get a blood-coloured left border so the log + map stay
   * visually connected when the user is scrubbing. Pass `null` (or
   * omit) for the live-view case where no row is selected.
   */
  selectedEventIndex?: number | null
  /**
   * Maximum number of events to show. The live feed reads a bounded wire tail
   * (WIRE_EVENT_LIMIT) so it's naturally small; the snapshot view passes a
   * finite cap since it groups the full `/events` log.
   */
  maxEvents?: number
  /**
   * Added to each row's local `eventIndex` to produce the ABSOLUTE index into
   * the full event log (what the time-machine reconstructs from). The live feed
   * groups a wire tail, so it passes `offset = eventCount - events.length`; the
   * snapshot sidebar groups the full log, so it passes 0 (the default). Without
   * this, a tail row's local index would mis-address the full log — the exact
   * bug the capped wire previously caused.
   */
  eventIndexOffset?: number
  /**
   * When true (the not-your-turn spectator feed) the log fetches the FULL event
   * history and infinite-scrolls: it renders the most recent PAGE_SIZE rows and
   * loads another PAGE_SIZE as you near the bottom, with an inline loader. When
   * false (default) it renders the live wire tail as-is (snapshot/complete).
   */
  infiniteScroll?: boolean
  /** The log filters (player, event kind, sort; lib/activityFilters). */
  filters?: ActivityFilterState
}

export function RecentActivitySidebar({
  gameId,
  state,
  players,
  orientation = 'left-sidebar',
  topContent,
  onViewAtEvent,
  onSeeAll,
  hideHeader = false,
  selectedEventIndex,
  maxEvents = Number.POSITIVE_INFINITY,
  eventIndexOffset = 0,
  infiniteScroll = false,
  filters = DEFAULT_LOG_FILTERS,
}: RecentActivitySidebarProps) {

  // Build slot-string → player map for display-name resolution + colour dots.
  const playerBySlot = useMemo(() => {
    const m = new Map<string, GamePlayerResponse>()
    for (const p of players) {
      m.set(String(p.slot), p)
    }
    return m
  }, [players])

  const resolve = useMemo(
    () => (playerId: string) =>
      playerBySlot.get(playerId)?.displayName ?? `Player ${playerId}`,
    [playerBySlot],
  )

  // Spectator feed pulls the FULL history so it can infinite-scroll; other
  // callers stay on the live wire tail. Disabled query = no fetch when off.
  const {
    events: fullEvents,
    isLoading: loadingFullLog,
    refetch: refetchFullLog,
  } = useGameEvents(gameId, { enabled: infiniteScroll })

  const [windowSize, setWindowSize] = useState(EVENT_PAGE_SIZE)

  // Keep the full log fresh: when new live events land (eventCount grows), pull
  // the delta so the newest rows appear at the top without leaving the feed.
  const liveEventCount =
    (state as { eventCount?: number }).eventCount ?? (state.events?.length ?? 0)
  useEffect(() => {
    if (infiniteScroll) void refetchFullLog()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveEventCount, infiniteScroll])

  // Prefer the fetched full log once it arrives; fall back to the live wire tail
  // while it loads. On the full log, row.eventIndex is already absolute (offset 0).
  const usingFullLog = infiniteScroll && fullEvents.length > 0
  const effectiveOffset = usingFullLog ? 0 : eventIndexOffset

  // Keyed on the events and the map, NOT on `state`. Every socket message writes
  // a new state object — a bot turn produces one every 400ms — and depending on
  // the whole thing re-grouped up to 200 events and minted brand-new row objects
  // for all of them, which defeats EventLogRow's React.memo. A patch that moves
  // an army, or a TURN_CHANGED that touches no event at all, now costs nothing
  // here.
  const stateEvents = state.events
  const stateMap = state.map

  // Public chat, interleaved into the feed. READ-ONLY from the query cache
  // (enabled: false): the game screen fetches it for members and the socket
  // appends live messages, so this never triggers a fetch of its own — and
  // therefore can't 403 for spectators (their feed just has no chat rows).
  const { data: chatMessages } = useGameMessages(gameId, false)
  const slotByUserId = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of players) m.set(p.userId, String(p.slot))
    return m
  }, [players])

  const allRows = useMemo<LogRow[]>(() => {
    const events = usingFullLog ? fullEvents : stateEvents ?? []
    const tName = makeTerritoryResolver(stateMap)
    const eventRows = groupEvents(events, resolve, tName) // newest-first
    if (events.length === 0) return eventRows
    // Only messages within this events window: the live feed groups a wire
    // TAIL, and chat older than its first event has no local anchor (and
    // would flood a compact feed with history that belongs to the full log).
    const firstEventTs = events[0]!.ts
    const windowMessages = (chatMessages ?? []).filter(
      (m) => Date.parse(m.createdAt) >= firstEventTs,
    )
    // eventIndex space here is LOCAL to `events`; renderRow adds
    // effectiveOffset to produce the absolute time-machine index, exactly as
    // it does for event rows.
    return mergeChatIntoLog({
      rowsNewestFirst: eventRows,
      messages: windowMessages,
      events,
      slotByUserId,
    })
  }, [usingFullLog, fullEvents, stateEvents, stateMap, resolve, chatMessages, slotByUserId])

  const filteredRows = useMemo(() => applyLogFilters(allRows, filters), [allRows, filters])
  const selectedRowIndex = selectedLogRowIndex(filteredRows, selectedEventIndex, effectiveOffset)
  const selectionScroll = useCenteredLogSelection(selectedRowIndex)
  const rows = useMemo<LogRow[]>(
    () => filteredRows.slice(0, selectedLogWindowSize(infiniteScroll ? windowSize : maxEvents, selectedRowIndex)),
    [filteredRows, infiniteScroll, windowSize, maxEvents, selectedRowIndex],
  )
  const hasMore = infiniteScroll && windowSize < filteredRows.length

  // Grow the window by a page when the user scrolls within ~5 rows of the bottom.
  const handleScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (!infiniteScroll) return
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent
    const distanceFromBottom = contentSize.height - contentOffset.y - layoutMeasurement.height
    if (shouldLoadMoreEvents({ distanceFromBottomPx: distanceFromBottom, windowSize, totalRows: filteredRows.length })) {
      setWindowSize((w) => growEventWindow(w, filteredRows.length))
    }
  }

  // Chat rows are the conversation itself: no navigation — a long press opens
  // reactions / copy / flag (ChatLogRow). Event rows open the time machine.
  // Chat rows never take the selected highlight — a chat row sharing its
  // anchor's index must not light up with the selected event.
  // Defined with useCallback so FlatList's row memoisation has a stable renderer.
  const chatSeen = useChatSeenViewability(useId())
  const renderRow = useCallback(
    ({ item: row }: { item: LogRow }) => {
      if (row.chat) return <ChatLogRow row={row} playerBySlot={playerBySlot} />
      // Map the row's local index to its ABSOLUTE index in the full log (what the
      // time-machine replays). Offset is 0 once the full log is loaded (indices
      // already absolute), else the wire-tail offset.
      const absoluteIndex = effectiveOffset + row.eventIndex
      const isSelected = isSelectedLogRow(row, selectedEventIndex, effectiveOffset)
      return onViewAtEvent ? (
        <TouchableOpacity
          onPress={() => onViewAtEvent(absoluteIndex)}
          accessibilityLabel={`View map at: ${row.primary}`}
          accessibilityRole="button"
          // Screen readers announce the snapshot's row; tests find it the same way.
          // (aria-selected, not accessibilityState: react-native-web only renders
          // the former, and React Native maps it to the same native state.)
          aria-selected={isSelected}
        >
          <EventLogRow variant="compact" row={row} playerBySlot={playerBySlot} isSelected={isSelected} />
        </TouchableOpacity>
      ) : (
        <EventLogRow variant="compact" row={row} playerBySlot={playerBySlot} isSelected={isSelected} />
      )
    },
    [effectiveOffset, selectedEventIndex, onViewAtEvent, playerBySlot],
  )

  const isBottom = orientation === 'bottom-panel'
  // Use useWindowDimensions only to derive the stable isDesktop boolean.
  // Rather than re-rendering on every intermediate rotation frame (which
  // useWindowDimensions fires on each), gate the boolean update through
  // useState so the component only re-renders when the boolean actually
  // flips across the DESKTOP_BREAKPOINT threshold — the same technique
  // useOrientation uses to gate re-renders on the landscape flip.
  const { width: screenWidth } = useWindowDimensions()
  const [isDesktop, setIsDesktop] = useState(() => screenWidth >= DESKTOP_BREAKPOINT)
  useEffect(() => {
    const next = screenWidth >= DESKTOP_BREAKPOINT
    setIsDesktop((prev) => (prev === next ? prev : next))
  }, [screenWidth])
  const landscapeWidth = recentActivitySidebarWidth(isDesktop)
  return (
    <View
      style={isBottom ? undefined : { width: landscapeWidth }}
      // bottom-panel mode is rendered only on the "not your turn"
      // portrait branch where there's no active-turn action bar
      // competing for vertical space. Flex it so the event list
      // fills every pixel below the map instead of getting cut off
      // at the previous 220px cap — entries past row 4 were
      // disappearing under the screen edge.
      className={
        isBottom
          ? 'flex-1 border-t border-olive bg-canvas'
          : 'border-r border-olive bg-canvas'
      }
      accessibilityLabel="Recent activity"
    >
      {topContent}

      {/* Header — fixed height (ACTIVITY_HEADER_HEIGHT).
          Hidden in the in-game feed (hideHeader): its log controls grow the
          log instead. */}
      {!hideHeader && (
        <View
          className="flex-row items-center justify-between px-3 border-b border-olive"
          style={{ height: ACTIVITY_HEADER_HEIGHT }}
        >
          <Text className="font-stencil text-ink text-xs uppercase tracking-widest">
            Recent activity
          </Text>
          {onSeeAll && (
            <TouchableOpacity
              onPress={onSeeAll}
              accessibilityLabel="See all activity"
              accessibilityRole="button"
            >
              <Text className="font-typed text-ink-soft text-xs">See all →</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Event list */}
      {/* VIRTUALISED. This was a plain ScrollView, so every row it held was a
          mounted native view — 40 to start, growing by 40 per page as you scroll,
          each row a TouchableOpacity plus several <Text> nodes and dice pills.
          With the row objects also being rebuilt on every 400ms bot patch, the
          screen re-rendered hundreds of text views a second, which is why buttons
          on a game screen felt laggy while the same buttons elsewhere did not.
          FlatList renders only what is on screen. */}
      <FlatList
        ref={selectionScroll.listRef}
        onLayout={selectionScroll.onLayout}
        onContentSizeChange={selectionScroll.onContentSizeChange}
        onScrollToIndexFailed={selectionScroll.onScrollToIndexFailed}
        className="flex-1"
        data={rows}
        keyExtractor={(row) => row.key}
        renderItem={renderRow}
        // Chat lines on screen count as read (lib/chatSeen).
        {...chatSeen}
        ItemSeparatorComponent={RowSeparator}
        contentContainerStyle={{ paddingVertical: 4 }}
        showsVerticalScrollIndicator={false}
        onScroll={infiniteScroll ? handleScroll : undefined}
        scrollEventThrottle={infiniteScroll ? 16 : undefined}
        // Keep the mounted set small; rows are short and cheap to mount.
        initialNumToRender={12}
        maxToRenderPerBatch={12}
        windowSize={5}
        removeClippedSubviews
        ListEmptyComponent={
          <View className="items-center py-8 px-3">
            <Text className="font-typed text-ink-soft text-xs italic text-center">
              {hasActiveFilters(filters) ? 'No events match these filters.' : 'No activity yet.'}
            </Text>
          </View>
        }
        ListFooterComponent={
          infiniteScroll && (loadingFullLog || hasMore) ? (
            <View className="items-center py-3">
              <ActivityIndicator size="small" color="#8a8c6e" />
            </View>
          ) : null
        }
      />
    </View>
  )
}
